import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import type { Client, Contract, ContractStatus } from '@shared/types';
import { useClient } from '../../hooks/useClients';
import { useContracts } from '../../hooks/useContracts';
import { clientService } from '../../services/clientService';
import { useConfirm } from '../../hooks/useConfirm';
import { ClientEditor } from './ClientEditor';
import { BulkContractEmailModal } from './BulkContractEmailModal';

const STATUS_LABELS: Record<ContractStatus, string> = {
  draft: 'Draft',
  internal_review: 'Internal Review',
  client_review: 'Client Review',
  signature: 'Signature',
  executed: 'Executed',
  archived: 'Archived',
};

const STATUS_PILL: Record<ContractStatus, string> = {
  draft: 'bg-yellow-100 text-yellow-700',
  internal_review: 'bg-blue-100 text-blue-700',
  client_review: 'bg-indigo-100 text-indigo-700',
  signature: 'bg-purple-100 text-purple-700',
  executed: 'bg-green-100 text-green-700',
  archived: 'bg-gray-100 text-gray-700',
};

// A contract is selectable for "Send for Client Review" if:
//   - it has a generated_document attached (otherwise there's nothing to send)
//   - it doesn't have a final_document set (final means canonical version
//     has moved on; sending the original would be misleading — server
//     refuses anyway, this is just a friendlier UX cue)
//   - it's in a status where the email is meaningful (anything pre-signature)
function isSelectable(c: Contract): { ok: boolean; reason?: string } {
  const meta = (c.metadata as any) ?? {};
  if (!meta.generated_document?.file_id) {
    return { ok: false, reason: 'Generate the document first.' };
  }
  if (meta.final_document) {
    return { ok: false, reason: 'Final document already uploaded.' };
  }
  if (c.status === 'executed' || c.status === 'archived') {
    return { ok: false, reason: `Status is ${c.status}.` };
  }
  return { ok: true };
}

export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { client, loading, reload } = useClient(id ?? '');
  const { contracts, loading: contractsLoading, reload: reloadContracts } = useContracts();
  const { confirm, ConfirmDialog } = useConfirm();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [emailOpen, setEmailOpen] = useState(false);

  // Match by clientId first; fall back to clientName for any legacy rows
  // that still have client_id NULL. The wizard now sets client_id at save
  // time so new contracts won't need this fallback.
  const clientContracts = useMemo(
    () =>
      contracts.filter((c) =>
        c.clientId
          ? c.clientId === client?.id
          : !!client?.name && c.clientName === client.name,
      ),
    [contracts, client?.id, client?.name],
  );

  const selectedContracts = useMemo(
    () => clientContracts.filter((c) => selected.has(c.id)),
    [clientContracts, selected],
  );

  const toggleContract = (c: Contract) => {
    const { ok } = isSelectable(c);
    if (!ok) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(c.id)) next.delete(c.id);
      else next.add(c.id);
      return next;
    });
  };

  const handleSave = async (updates: Partial<Client>) => {
    if (!client) return;
    setSaving(true);
    const toastId = toast.loading('Saving client…');
    try {
      await clientService.updateClient(client.id, updates);
      toast.success('Client updated', { id: toastId });
      setEditing(false);
      await reload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Save failed', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!client) return;
    const ok = await confirm({
      title: 'Delete Client',
      message: `Permanently delete "${client.name}"? Contracts referencing this client will keep their record but the client row will be removed. This cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    const toastId = toast.loading('Deleting client…');
    try {
      await clientService.deleteClient(client.id);
      toast.success('Client deleted', { id: toastId });
      navigate('/clients');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Delete failed', { id: toastId });
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-3">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to Clients
        </button>
        <p className="text-sm text-gray-500">Client not found.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        <button
          onClick={() => navigate('/clients')}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="w-4 h-4" /> Back to Clients
        </button>

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{client.name}</h1>
            <p className="mt-1 text-sm text-gray-500">
              {[client.city, client.state].filter(Boolean).join(', ') || 'No address set'}
              {client.industry ? ` · ${client.industry}` : ''}
            </p>
          </div>
          {!editing && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50"
              >
                Edit Client
              </button>
            </div>
          )}
        </div>

        {/* Contracts */}
        {!editing && (
          <section className="bg-white shadow-sm rounded-lg">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">Contracts</h2>
                <p className="text-xs text-gray-500">
                  Select one or more to send for client review.
                </p>
              </div>
              <button
                onClick={() => setEmailOpen(true)}
                disabled={selected.size === 0}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
              >
                <EnvelopeIcon className="w-4 h-4" />
                Send for Client Review{selected.size > 0 ? ` (${selected.size})` : ''}
              </button>
            </div>

            {contractsLoading ? (
              <div className="px-6 py-8 text-sm text-gray-500">Loading…</div>
            ) : clientContracts.length === 0 ? (
              <div className="px-6 py-8 text-sm text-gray-500">
                No contracts yet for this client.{' '}
                <button
                  onClick={() => navigate('/contracts/new')}
                  className="text-primary-600 hover:underline"
                >
                  Create one
                </button>
                .
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {clientContracts.map((c) => {
                  const sel = isSelectable(c);
                  const checked = selected.has(c.id);
                  const meta = (c.metadata as any) ?? {};
                  const sentTimes =
                    (Array.isArray(meta.emails_sent) ? meta.emails_sent.length : 0) || 0;
                  const approvedInternally =
                    Array.isArray(meta.internal_review_approvals) &&
                    meta.internal_review_approvals.length > 0;
                  return (
                    <li
                      key={c.id}
                      className={`flex items-center gap-3 px-4 sm:px-6 py-3 ${
                        sel.ok ? 'hover:bg-gray-50' : 'opacity-70'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleContract(c)}
                        disabled={!sel.ok}
                        title={sel.reason}
                        className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 disabled:cursor-not-allowed"
                      />
                      <button
                        onClick={() => navigate(`/contracts/${c.id}`)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <p className="truncate text-sm font-medium text-gray-900">
                          {c.title || c.clientName || 'Untitled contract'}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                          <span
                            className={`px-1.5 py-0.5 rounded font-medium ${
                              STATUS_PILL[c.status]
                            }`}
                          >
                            {STATUS_LABELS[c.status]}
                          </span>
                          {c.templateType && <span>· {c.templateType}</span>}
                          {c.status === 'internal_review' && approvedInternally && (
                            <span className="text-green-700">· ✓ Approved — ready to send</span>
                          )}
                          {sentTimes > 0 && (
                            <span>
                              · Sent {sentTimes}×
                            </span>
                          )}
                          {meta.final_document && (
                            <span className="text-green-700">· ✓ Final uploaded</span>
                          )}
                          {!sel.ok && sel.reason && (
                            <span className="text-amber-700">· {sel.reason}</span>
                          )}
                        </div>
                      </button>
                      <button
                        onClick={() => navigate(`/contracts/${c.id}`)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Open contract"
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* Profile + contacts + certs — the existing editor component, in
            view mode initially, edit mode when toggled. Reuses everything
            already built (form fields, contact CRUD, ACORD cert list,
            enrich button). */}
        <section>
          <ClientEditor
            client={client}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
            saving={saving}
          />
        </section>

        {editing && (
          <div className="flex justify-end">
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-md border border-red-200"
            >
              Delete Client
            </button>
          </div>
        )}

        <BulkContractEmailModal
          isOpen={emailOpen}
          onClose={() => setEmailOpen(false)}
          client={client}
          contracts={selectedContracts}
          onSent={() => {
            setSelected(new Set());
            void reloadContracts();
          }}
        />

        <ConfirmDialog />
      </div>
    </div>
  );
}
